import * as React from 'react';
import {Query, Mutation} from 'react-apollo';

import type {
    GetClosedLeadEmailAutomation as GetClosedLeadEmailAutomationQuery,
    GetClosedLeadEmailAutomationVariables as GetClosedLeadEmailAutomationQueryVariables,
    CreateClosedLeadEmailAutomation as CreateClosedLeadEmailAutomationMutation,
    CreateClosedLeadEmailAutomationVariables as CreateClosedLeadEmailAutomationMutationVariables,
    CreateClosedLeadEmailAutomationInputFieldInput,
    UpdateClosedLeadEmailAutomation as UpdateClosedLeadEmailAutomationMutation,
    UpdateClosedLeadEmailAutomationVariables as UpdateClosedLeadEmailAutomationMutationVariables,
    UpdateClosedLeadEmailAutomationInputFieldInput,
    DeleteClosedLeadEmailAutomation as DeleteClosedLeadEmailAutomationMutation,
    DeleteClosedLeadEmailAutomationVariables as DeleteClosedLeadEmailAutomationMutationVariables,
    DeleteClosedLeadEmailAutomationInputFieldInput,
} from 'nutshell-graphql-types';

import {ClosedLeadConstantContactSubscriptionForm} from '../../forms/closed-lead-constant-contact-subscription-form';
import {ClosedLeadMailChimpSubscriptionForm} from '../../forms/closed-lead-mailchimp-subscription-form';
import CREATE_CLOSED_LEAD_EMAIL_AUTOMATION from '../../graphql/mutations/create-closed-lead-email-automation.graphql';
import DELETE_CLOSED_LEAD_EMAIL_AUTOMATION from '../../graphql/mutations/delete-closed-lead-email-automation.graphql';
import UPDATE_CLOSED_LEAD_EMAIL_AUTOMATION from '../../graphql/mutations/update-closed-lead-email-automation.graphql';
import GET_CLOSED_LEAD_EMAIL_AUTOMATION from '../../graphql/queries/get-closed-lead-email-automation.graphql';

import {SalesAutomationEditorSidebar} from './sidebar/sales-automation-editor-sidebar';

export type EmailListType = 'CONSTANT_CONTACT' | 'MAILCHIMP';

type GraphQLProps = {
    subscription: GetClosedLeadEmailAutomationQuery['closedLeadEmailAutomation'] | null | undefined;
    isFetchingLatestVersion: boolean;
    createSubscription: (arg1: CreateClosedLeadEmailAutomationInputFieldInput) => Promise<any>;
    updateSubscription: (arg1: UpdateClosedLeadEmailAutomationInputFieldInput) => Promise<any>;
    deleteSubscription: (arg1: DeleteClosedLeadEmailAutomationInputFieldInput) => Promise<any>;
};

type Props = OwnProps & GraphQLProps;

class ClosedLeadEmailAutomationEditorComponent extends React.PureComponent<Props> {
    sidebarFormSubmitBtnRef: HTMLInputElement | null | undefined;

    render() {
        const typeText = this.props.type === 'MAILCHIMP' ? 'Mailchimp' : 'Constant Contact';
        const Component =
            this.props.type === 'MAILCHIMP'
                ? ClosedLeadMailChimpSubscriptionForm
                : ClosedLeadConstantContactSubscriptionForm;

        return (
            <SalesAutomationEditorSidebar
                onSave={this.handleSidebarFormSave}
                title={
                    this.props.isNew
                        ? `New ${typeText} subscription`
                        : `Edit ${typeText} subscription`
                }
            >
                <Component
                    formSubmitBtnRef={(node) => {
                        this.sidebarFormSubmitBtnRef = node;
                    }}
                    isFetchingLatestVersion={this.props.isFetchingLatestVersion}
                    subscription={this.props.subscription}
                    onSave={this.handleSave}
                    onDelete={this.handleDelete}
                />
            </SalesAutomationEditorSidebar>
        );
    }

    handleSidebarFormSave = () => {
        if (this.sidebarFormSubmitBtnRef) {
            this.sidebarFormSubmitBtnRef.click();
        }
    };

    // @ts-expect-error
    handleSave = (selectedListId) => {
        if (this.props.subscription) {
            return this.props.updateSubscription({
                id: this.props.subscription.id,
                emailListExternalListId: selectedListId,
            });
        } else {
            // This is a refinement for flow, to guarantee that the parentId is an
            // expected outcomeType
            if (
                this.props.parentId !== 'LEAD_CANCELLED' &&
                this.props.parentId !== 'LEAD_WON' &&
                this.props.parentId !== 'LEAD_LOST'
            ) {
                return null;
            }

            return this.props.createSubscription({
                stagesetId: this.props.stagesetId,
                outcomeType: this.props.parentId,
                emailListExternalListId: selectedListId,
                emailListType: this.props.type,
            });
        }
    };

    handleDelete = (id: string) => {
        return this.props.deleteSubscription({id});
    };
}

class SubscriptionQuery extends Query<
    GetClosedLeadEmailAutomationQuery,
    GetClosedLeadEmailAutomationQueryVariables
> {}

class CreateMutation extends Mutation<
    CreateClosedLeadEmailAutomationMutation,
    CreateClosedLeadEmailAutomationMutationVariables
> {}
class UpdateMutation extends Mutation<
    UpdateClosedLeadEmailAutomationMutation,
    UpdateClosedLeadEmailAutomationMutationVariables
> {}
class DeleteMutation extends Mutation<
    DeleteClosedLeadEmailAutomationMutation,
    DeleteClosedLeadEmailAutomationMutationVariables
> {}

type OwnProps = {
    isNew: boolean;
    stagesetId: string;
    parentId: string;
    entityId: string | null | undefined;
    type: EmailListType;
};

export const ClosedLeadEmailAutomationEditor = (props: OwnProps) => (
    <SubscriptionQuery
        query={GET_CLOSED_LEAD_EMAIL_AUTOMATION}
        skip={!props.entityId}
        variables={{emailAutomationId: props.entityId || ''}}
        // @ts-expect-error
        fetchPolicy='cache-and-network'
    >
        {({data, loading}) => {
            const closedLeadEmailAutomation = data ? data.closedLeadEmailAutomation : null;

            return (
                <CreateMutation mutation={CREATE_CLOSED_LEAD_EMAIL_AUTOMATION}>
                    {(createSubscription) => (
                        <UpdateMutation mutation={UPDATE_CLOSED_LEAD_EMAIL_AUTOMATION}>
                            {(updateSubscription) => (
                                <DeleteMutation mutation={DELETE_CLOSED_LEAD_EMAIL_AUTOMATION}>
                                    {(deleteSubscription) => (
                                        <ClosedLeadEmailAutomationEditorComponent
                                            {...props}
                                            isFetchingLatestVersion={loading}
                                            subscription={closedLeadEmailAutomation}
                                            createSubscription={(emailAutomation) => {
                                                return createSubscription({
                                                    variables: {emailAutomation},
                                                });
                                            }}
                                            updateSubscription={(emailAutomation) => {
                                                return updateSubscription({
                                                    variables: {
                                                        emailAutomation,
                                                    },
                                                });
                                            }}
                                            deleteSubscription={(emailAutomation) => {
                                                return deleteSubscription({
                                                    variables: {
                                                        emailAutomation,
                                                    },
                                                });
                                            }}
                                        />
                                    )}
                                </DeleteMutation>
                            )}
                        </UpdateMutation>
                    )}
                </CreateMutation>
            );
        }}
    </SubscriptionQuery>
);
